import {
  Barcode,
  Camera,
  Dumbbell,
  History,
  ImageUp,
  Mic,
  NotebookPen,
  Plus,
  Scale,
  ScanLine,
  Search,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { useLoggingFlow } from "../../lib/logging-flow";
import type { LoggingFlowKind } from "../../lib/logging-flow";
import { isMobileDevice, usePhotoPicker } from "../../lib/photo-picker";

// Quick Log launcher action descriptor (plan todo 10). Pure data so the unit
// suite can assert testid -> flow-kind routing without a DOM renderer.
// Order is contractual: most-common capture methods first.
export interface QuickLogAction {
  readonly kind: LoggingFlowKind;
  readonly testId: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly section: "primary" | "secondary" | "more";
}

export const QUICK_LOG_ACTIONS: readonly QuickLogAction[] = [
  { kind: "photo", testId: "ql-photo", label: "Take meal photo", icon: Camera, section: "primary" },
  { kind: "upload", testId: "ql-upload", label: "Upload meal photo", icon: ImageUp, section: "primary" },
  { kind: "text", testId: "ql-text", label: "Describe meal", icon: NotebookPen, section: "primary" },
  { kind: "voice", testId: "ql-voice", label: "Voice log", icon: Mic, section: "primary" },
  { kind: "barcode", testId: "ql-barcode", label: "Scan barcode", icon: Barcode, section: "primary" },
  { kind: "search", testId: "ql-search", label: "Search food", icon: Search, section: "primary" },
  { kind: "library", testId: "ql-recent", label: "Recent and saved meals", icon: History, section: "secondary" },
  { kind: "exercise", testId: "ql-exercise", label: "Log exercise", icon: Dumbbell, section: "secondary" },
  { kind: "weight", testId: "ql-weight", label: "Log weight", icon: Scale, section: "secondary" },
  { kind: "label", testId: "ql-label", label: "Scan nutrition label", icon: ScanLine, section: "more" },
  { kind: "quickAdd", testId: "ql-quick-add", label: "Quick add", icon: Plus, section: "more" },
];

const ACTIONS_BY_SECTION = {
  primary: QUICK_LOG_ACTIONS.filter((action) => action.section === "primary"),
  secondary: QUICK_LOG_ACTIONS.filter((action) => action.section === "secondary"),
  more: QUICK_LOG_ACTIONS.filter((action) => action.section === "more"),
} as const;

// Pure selection routing (unit-testable without a DOM): which launcher kinds
// open the native OS picker directly vs. starting an in-app flow screen.
// "upload" always uses the library picker; "photo" uses the native camera
// capture sheet only on mobile — desktop keeps the webcam flow screen.
export type QuickLogSelection =
  | { readonly type: "nativePicker"; readonly picker: "library" | "capture" }
  | { readonly type: "flow" };

export function resolveQuickLogSelection(
  kind: LoggingFlowKind,
  mobile: boolean,
): QuickLogSelection {
  if (kind === "upload") return { type: "nativePicker", picker: "library" };
  if (kind === "photo" && mobile) return { type: "nativePicker", picker: "capture" };
  return { type: "flow" };
}

export function QuickLogLauncher() {
  const { launcherOpen, closeLauncher, startFlow } = useLoggingFlow();
  const { openLibraryPicker, openCapturePicker } = usePhotoPicker();

  if (!launcherOpen) return null;

  const handleSelect = (kind: LoggingFlowKind) => {
    const selection = resolveQuickLogSelection(kind, isMobileDevice());
    if (selection.type === "nativePicker") {
      // The chosen file arrives via the picker's startFlow(kind, { file });
      // a picker cancel leaves the app exactly where it was.
      closeLauncher();
      if (selection.picker === "library") {
        openLibraryPicker();
      } else {
        openCapturePicker();
      }
      return;
    }
    // Contract: every other launcher item calls startFlow + closeLauncher. The
    // reducer's startFlow also closes the launcher; the explicit close keeps
    // the intent obvious at the call site.
    startFlow(kind);
    closeLauncher();
  };

  return (
    <>
      <div
        data-testid="quick-log-scrim"
        className="fixed inset-0 z-40 bg-black/50"
        onClick={closeLauncher}
        aria-hidden="true"
      />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex max-h-[85vh] flex-col justify-end p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
        <div
          data-testid="quick-log-launcher"
          role="dialog"
          aria-label="Quick log"
          className="pointer-events-auto mx-auto w-full max-w-xl overflow-y-auto rounded-2xl border border-border bg-surface shadow-lg ob-sheet-up"
        >
          <div className="flex items-center justify-between px-5 pt-4">
            <h2 className="text-lg font-semibold text-text">Quick log</h2>
            <button
              type="button"
              data-testid="quick-log-close"
              aria-label="Close quick log"
              onClick={closeLauncher}
              className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted active:bg-muted active:scale-[0.98] select-none"
            >
              <X size={20} strokeWidth={2} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 px-4 pt-2">
            {ACTIONS_BY_SECTION.primary.map((action) => (
              <button
                key={action.testId}
                type="button"
                data-testid={action.testId}
                onClick={() => handleSelect(action.kind)}
                className={cn(
                  "flex min-h-[56px] items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2",
                  "text-left text-sm font-medium text-text",
                  "transition-all duration-200 ease-out active:scale-[0.98] active:bg-muted select-none",
                )}
              >
                <action.icon size={22} strokeWidth={2} className="shrink-0 text-accent" />
                <span>{action.label}</span>
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-2 px-4 pt-3">
            {ACTIONS_BY_SECTION.secondary.map((action) => (
              <button
                key={action.testId}
                type="button"
                data-testid={action.testId}
                onClick={() => handleSelect(action.kind)}
                className={cn(
                  "flex min-h-[56px] items-center gap-3 rounded-xl px-3 py-2",
                  "text-left text-sm font-medium text-text",
                  "transition-all duration-200 ease-out active:scale-[0.98] active:bg-muted select-none",
                )}
              >
                <action.icon size={22} strokeWidth={2} className="shrink-0 text-text-muted" />
                <span>{action.label}</span>
              </button>
            ))}
          </div>

          <details className="px-4 pb-4 pt-2" data-testid="quick-log-more">
            <summary className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl px-3 text-sm font-medium text-text-muted active:bg-muted select-none">
              More
            </summary>
            <div className="flex flex-col gap-2 pt-1">
              {ACTIONS_BY_SECTION.more.map((action) => (
                <button
                  key={action.testId}
                  type="button"
                  data-testid={action.testId}
                  onClick={() => handleSelect(action.kind)}
                  className={cn(
                    "flex min-h-[56px] items-center gap-3 rounded-xl px-3 py-2",
                    "text-left text-sm font-medium text-text",
                    "transition-all duration-200 ease-out active:scale-[0.98] active:bg-muted select-none",
                  )}
                >
                  <action.icon size={22} strokeWidth={2} className="shrink-0 text-text-muted" />
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          </details>
        </div>
      </div>
    </>
  );
}
